import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Toolbar, Paper, Typography } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { AppBarComponent } from '@/components/layout/AppBarComponent';
import { Sidebar } from '@/components/layout/Sidebar';
import { useViewPort, useAuth } from '@/hooks';
import { USER_ROLES } from '@/constants/app';

const DRAWER_WIDTH_COLLAPSED = 64;

export const DashboardLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isMobile } = useViewPort();
  const { user } = useAuth();
  const pageContentRef = useRef<HTMLDivElement>(null);

  // Check if user has admin privileges
  const isAdmin = user?.role === USER_ROLES.Booking_SuperAdmin || user?.role === USER_ROLES.Booking_Admin;

  const handleMobileMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileOpen(false);
  };

  // Height logging effect
  useEffect(() => {
    const logPageContentHeight = () => {
      if (pageContentRef.current) {
        const height = pageContentRef.current.clientHeight;
        const scrollHeight = pageContentRef.current.scrollHeight;
        const offsetHeight = pageContentRef.current.offsetHeight;
        
        console.log('📐 Page Content Height Measurements:', {
          clientHeight: height,
          scrollHeight: scrollHeight,
          offsetHeight: offsetHeight,
          availableHeight: `${height}px`,
          timestamp: new Date().toLocaleTimeString()
        });
      }
    };

    // Log height on mount and when window resizes
    logPageContentHeight();
    window.addEventListener('resize', logPageContentHeight);
    
    // Also log after a short delay to catch layout changes
    const timeoutId = setTimeout(logPageContentHeight, 100);

    return () => {
      window.removeEventListener('resize', logPageContentHeight);
      clearTimeout(timeoutId);
    };
  }, [isMobile]); // Re-run when mobile state changes

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBarComponent onMobileMenuToggle={handleMobileMenuToggle} showMobileMenu={isAdmin} />

      {/* Sidebar - Only show for admin users */}
      {isAdmin && (
        <Sidebar
          mobileOpen={mobileOpen}
          onMobileClose={handleMobileMenuClose}
        />
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          position: 'fixed',
          top: 0,
          left: {
            xs: 0,
            sm: isAdmin ? `${DRAWER_WIDTH_COLLAPSED + 16}px` : 0, // Only offset if admin has sidebar
          },
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        {/* Toolbar spacing */}
        <Toolbar />
        
        {/* Page Content */}
        <Box
          ref={pageContentRef}
          sx={{
            flexGrow: 1,
            px: isMobile?0: 3,
            pt:2,
            py:2,
            overflow: 'auto',
            pb: 6, // Add padding at bottom to prevent content from being hidden behind footer
          }}
        >
          <Container maxWidth="xl" sx={{ height: '99%' }}>
            <Paper
              elevation={0}
              sx={{
                px: {xs: isMobile?2:0,  sm:isMobile?2:0 , md:3.5},
                py: {xs: isMobile?2:0,  sm:isMobile?2:0 , md:3},
                borderRadius: 2,
                minHeight: 'calc(100vh - 200px)',
                bgcolor: 'background.paper',
              }}
            >
              <Outlet />
            </Paper>
          </Container>
        </Box>

        {/* Sticky Footer */}
        <Box
          component="footer"
          sx={{
            py: 1.5,
            px: 3,
            textAlign: 'center',
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            For any clarification/update please email to{' '}
            <Typography
              component="a"
              href="mailto:fmhbookings@dglofmadras.org"
              variant="body2"
              color="primary"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              fmhbookings@dglofmadras.org
            </Typography>
            {/* {' & '}
            <Typography
              component="a"
              href="mailto:dglmadras@gmail.com"
              variant="body2"
              color="primary"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              dglmadras@gmail.com
            </Typography> */}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};